import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Empty, Input, Select, Label, Textarea } from "@/components/Primitives";
import { StatusPill } from "@/components/StatusPill";
import { Plus, Clock, X, Pencil } from "lucide-react";
import { toast } from "sonner";

const COLS = [
  { id: "todo", label: "To do", dot: "bg-[hsl(var(--text-muted))]" },
  { id: "in_progress", label: "In progress", dot: "bg-[hsl(var(--primary))]" },
  { id: "review", label: "Review", dot: "bg-[hsl(var(--warning))]" },
  { id: "done", label: "Done", dot: "bg-[hsl(var(--success))]" },
];

const EMPTY = {
  title: "", description: "", priority: "Normal", status: "todo",
  assignee: "", assignee_id: "",
  customer_id: "", customer_name: "",
  estimated_hours: 0, hours_logged: 0, due_date: "",
};

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [team, setTeam] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dragTaskId, setDragTaskId] = useState(null);

  const load = async () => {
    const [ts, tm, cs] = await Promise.all([
      api.get("/tasks"), api.get("/team"), api.get("/customers"),
    ]);
    setTasks(ts.data || []);
    setTeam(tm.data || []);
    setCustomers(cs.data || []);
  };
  useEffect(() => { load(); }, []);

  const move = async (id, status) => {
    setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, status } : t)));
    await api.patch(`/tasks/${id}`, { status });
  };

  const onDragStart = (id) => setDragTaskId(id);
  const onDrop = (status) => {
    if (!dragTaskId) return;
    move(dragTaskId, status);
    setDragTaskId(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Delivery · Tasks"
        title="Tasks"
        subtitle="Your internal task board. Drag cards to move, click to edit."
        actions={<Button onClick={() => setEditing("new")} data-testid="new-task-btn"><Plus className="w-4 h-4" strokeWidth={2}/>New task</Button>}
      />
      <Card>
        <div className="p-5 overflow-x-auto">
          <div className="grid grid-cols-4 gap-4 min-w-[880px]">
            {COLS.map((c) => (
              <div
                key={c.id}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2", "ring-[hsl(var(--primary))]"); }}
                onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-[hsl(var(--primary))]")}
                onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("ring-2", "ring-[hsl(var(--primary))]"); onDrop(c.id); }}
                className="rounded-xl bg-[hsl(var(--background))] border border-[hsl(var(--border))] p-3 min-h-[260px] transition-all"
                data-testid={`task-column-${c.id}`}
              >
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                    <div className="text-[12px] font-semibold">{c.label}</div>
                  </div>
                  <div className="text-[10px] font-mono text-[hsl(var(--text-muted))] bg-[hsl(var(--surface))] rounded px-1.5 py-0.5">
                    {tasks.filter((t) => t.status === c.id).length}
                  </div>
                </div>
                <div className="space-y-2">
                  {tasks.filter((t) => t.status === c.id).map((t) => (
                    <div
                      key={t.id}
                      draggable
                      onDragStart={() => onDragStart(t.id)}
                      onDragEnd={() => setDragTaskId(null)}
                      className="p-3 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] transition-all group cursor-grab active:cursor-grabbing"
                      data-testid="task-card"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <button onClick={() => setEditing(t)} className="text-[13px] font-medium leading-snug text-left flex-1 hover:text-[hsl(var(--primary))]">
                          {t.title}
                        </button>
                        <StatusPill variant={t.priority === "High" ? "warning" : t.priority === "Low" ? "default" : "primary"}>{t.priority}</StatusPill>
                      </div>
                      <div className="mt-2 text-[11px] text-[hsl(var(--text-muted))] flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5">
                          {(() => {
                            const m = team.find((x) => x.id === t.assignee_id) || team.find((x) => (x.name || "").toLowerCase() === (t.assignee || "").toLowerCase());
                            if (m) return <>
                              <img src={m.avatar_url || "/brand/soubor-one-mark.png"} onError={(e) => { e.currentTarget.src = "/brand/soubor-one-mark.png"; }} alt="" className="w-5 h-5 rounded-full object-cover ring-1 ring-[hsl(var(--border))]"/>
                              <span>{m.name}</span>
                            </>;
                            return <span>{t.assignee || "—"}</span>;
                          })()}
                        </span>
                        {(t.estimated_hours > 0 || t.hours_logged > 0) && (
                          <span className="inline-flex items-center gap-1 font-mono">
                            <Clock className="w-3 h-3" strokeWidth={1.8}/>
                            {t.hours_logged}h / {t.estimated_hours}h
                          </span>
                        )}
                      </div>
                      {t.estimated_hours > 0 && (
                        <div className="mt-1.5 h-1 rounded-full bg-[hsl(var(--surface-hover))] overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.min(100, (t.hours_logged / t.estimated_hours) * 100)}%`,
                              background: t.hours_logged > t.estimated_hours ? "#F43F5E" : "hsl(var(--primary))",
                            }}
                          />
                        </div>
                      )}
                      <div className="mt-2 flex gap-1 flex-wrap opacity-70 group-hover:opacity-100 transition-opacity">
                        {COLS.filter((cc) => cc.id !== c.id).map((cc) => (
                          <button key={cc.id} onClick={() => move(t.id, cc.id)} className="text-[10px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] px-1.5 py-0.5 rounded border border-[hsl(var(--border))]">→ {cc.label}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {editing && (
        <TaskModal
          initial={editing === "new" ? EMPTY : editing}
          team={team}
          customers={customers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDelete={async () => {
            if (!window.confirm("Delete this task?")) return;
            await api.delete(`/tasks/${editing.id}`);
            toast.success("Task deleted");
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function TaskModal({ initial, team, customers, onClose, onSaved, onDelete }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const isNew = !initial.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setAssignee = (memberId) => {
    if (!memberId) { set("assignee_id", ""); set("assignee", ""); return; }
    const m = team.find((x) => x.id === memberId);
    setForm((f) => ({ ...f, assignee_id: memberId, assignee: m?.name || "" }));
  };

  const setCustomer = (cid) => {
    if (!cid) { set("customer_id", ""); set("customer_name", ""); return; }
    const c = customers.find((x) => x.id === cid);
    setForm((f) => ({ ...f, customer_id: cid, customer_name: c?.company || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_hours: parseFloat(form.estimated_hours) || 0,
        hours_logged: parseFloat(form.hours_logged) || 0,
      };
      if (isNew) {
        await api.post("/tasks", payload);
        toast.success("Task created");
      } else {
        await api.patch(`/tasks/${initial.id}`, payload);
        toast.success("Task updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-2xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[90vh] overflow-y-auto"
        data-testid="task-modal"
      >
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h3 className="text-lg font-semibold">{isNew ? "New task" : "Edit task"}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>

        <div className="p-6 space-y-5">
          <div><Label>Title *</Label><Input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="task-title"/></div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)}/></div>

          <div className="grid grid-cols-3 gap-4">
            <div><Label>Status</Label>
              <Select value={form.status} onChange={(e) => set("status", e.target.value)}>
                {COLS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </Select>
            </div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onChange={(e) => set("priority", e.target.value)}>
                <option value="Low">Low</option><option value="Normal">Normal</option><option value="High">High</option>
              </Select>
            </div>
            <div><Label>Due date</Label><Input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)}/></div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><Label>Assignee</Label>
              <Select value={form.assignee_id || ""} onChange={(e) => setAssignee(e.target.value)} data-testid="task-assignee">
                <option value="">Unassigned</option>
                {team.filter((m) => m.active !== false).map((m) => (
                  <option key={m.id} value={m.id}>{m.name} {m.type === "external" ? "(external)" : ""}</option>
                ))}
              </Select>
            </div>
            <div><Label>Customer</Label>
              <Select value={form.customer_id || ""} onChange={(e) => setCustomer(e.target.value)}>
                <option value="">None (internal)</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.company}</option>)}
              </Select>
            </div>
          </div>

          <div className="rounded-lg border border-[hsl(var(--border))] p-4 bg-[hsl(var(--background))]">
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mb-3 inline-flex items-center gap-1.5">
              <Clock className="w-3 h-3" strokeWidth={2}/>Hours
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Estimated</Label><Input type="number" min="0" step="0.25" value={form.estimated_hours} onChange={(e) => set("estimated_hours", e.target.value)} data-testid="task-estimated-hours"/></div>
              <div><Label>Logged</Label><Input type="number" min="0" step="0.25" value={form.hours_logged} onChange={(e) => set("hours_logged", e.target.value)} data-testid="task-hours-logged"/></div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-between gap-2">
          <div>
            {!isNew && (
              <Button type="button" variant="ghost" onClick={onDelete} className="text-rose-600 hover:text-rose-700"><X className="w-4 h-4"/>Delete</Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="task-save-btn">{saving ? "Saving…" : isNew ? "Create task" : "Save"}</Button>
          </div>
        </div>
      </form>
    </div>
  );
}
